import React from 'react';
import {
	Broadcast,
	BroadcastStatus,
	IN_PROGRESS_STATUSES,
	StreamStatus,
} from '../../../stores/types/broadcasting';
import { Probe, ProbeStatus } from '../types';
import HealthPanel from '../health-panel';
import CopyValue from '../copy-value';
import StatusPill from '../status-pill';
import {
	formatAgo, formatDurationMs, formatElapsed, formatTimestamp, redactStreamUrl,
} from '../format';

interface Props {
	probe: Probe<Broadcast> | undefined;
}

/**
 * An `active` Egress whose updatedAt has stopped advancing is stalled. LiveKit
 * touches the record often enough that a gap this long means it is not running.
 */
export const STALE_EGRESS_MS = 60000;

const streamStatusToProbeStatus = (status?: StreamStatus): ProbeStatus => {
	if (status === StreamStatus.failed) return ProbeStatus.Error;
	if (status === StreamStatus.active) return ProbeStatus.Ok;
	return ProbeStatus.Empty;
};

/**
 * Spielberg broadcast, which is a LiveKit web egress. Sync refreshes this entity
 * against Spielberg on read, so this is the egress service's own view rather than
 * a cached copy from whenever the broadcast started.
 *
 * There is no throughput or bitrate here on purpose: LiveKit does not expose
 * per-egress media metrics over the API, only Prometheus metrics on the egress
 * service itself. What it does expose is why an egress failed, what page it is
 * capturing, and how recently it was touched - which is what this panel shows.
 */
export default function BroadcastPanel({ probe }: Props) {
	const broadcast = probe?.data;
	const outputs = broadcast?.outputs ?? [];
	const encoding = broadcast?.encodingOptions;
	const isRunning = !!broadcast?.status && IN_PROGRESS_STATUSES.includes(broadcast.status);
	const hasFailed = broadcast?.status === BroadcastStatus.failed
		|| broadcast?.status === BroadcastStatus.aborted
		|| broadcast?.status === BroadcastStatus.limitReached;

	// A running egress that has gone quiet is the failure mode a bare status
	// cannot show: it still says `active` while nothing is being delivered.
	const isStalled = isRunning
		&& !!broadcast?.updatedAt
		&& Date.now() - broadcast.updatedAt > STALE_EGRESS_MS;

	const failedOutputs = outputs.filter((output) => output.status === StreamStatus.failed);

	/* eslint-disable-next-line no-nested-ternary */
	const statusPill = hasFailed
		? ProbeStatus.Error
		/* eslint-disable-next-line no-nested-ternary */
		: isStalled
			? ProbeStatus.Degraded
			: isRunning ? ProbeStatus.Ok : ProbeStatus.Empty;

	return (
		<HealthPanel
			title='Broadcast egress'
			source='sync → spielberg · livekit web egress'
			probe={probe}
		>
			{/* Failure reason first: it is the reason a producer opened this panel. */}
			{!!broadcast?.error && (
				<p className='health-banner health-banner--error'>
					{broadcast.error}
					{!!broadcast.errorCode && ` (code ${broadcast.errorCode})`}
				</p>
			)}
			{!!broadcast?.details && !broadcast?.error && (
				<p className='health-panel__note'>{broadcast.details}</p>
			)}
			{isStalled && (
				<p className='health-banner health-banner--warn'>
					Egress reports <strong>{broadcast?.status}</strong> but LiveKit has not touched it
					since {formatAgo(broadcast!.updatedAt!)} — likely stalled.
				</p>
			)}

			<dl className='health-facts'>
				<dt>Status</dt>
				<dd>
					<StatusPill status={statusPill} label={broadcast?.status ?? 'unknown'} />
					{!!failedOutputs.length && !hasFailed && (
						<span className='health-tag health-tag--warn'>
							{failedOutputs.length} of {outputs.length} destinations failed
						</span>
					)}
				</dd>
				<dt>Egress id</dt>
				<dd><CopyValue value={broadcast?.id ?? ''} mono /></dd>
				<dt>Capturing</dt>
				<dd><CopyValue value={broadcast?.sourceUrl ?? ''} mono /></dd>
				<dt>Started</dt>
				<dd>
					{formatTimestamp(broadcast?.startTime)}
					{!!broadcast?.startTime && !broadcast?.endTime && (
						<span className='health-tag'>{formatElapsed(broadcast.startTime)} elapsed</span>
					)}
				</dd>
				{!!broadcast?.endTime && (
					<>
						<dt>Ended</dt>
						<dd>{formatTimestamp(broadcast.endTime)}</dd>
					</>
				)}
				<dt>Egress updated</dt>
				<dd>
					{broadcast?.updatedAt ? formatAgo(broadcast.updatedAt) : '—'}
					{isStalled && <span className='health-tag health-tag--warn'>stale</span>}
				</dd>
				{encoding && (
					<>
						<dt>Encoding</dt>
						<dd>
							{encoding.preset !== undefined ? (
								`preset ${encoding.preset}`
							) : (
								<>
									{encoding.width}×{encoding.height}
									{encoding.framerate ? ` @ ${encoding.framerate}fps` : ''}
									{encoding.videoBitrate ? ` · ${encoding.videoBitrate}kbps video` : ''}
									{encoding.audioBitrate ? ` · ${encoding.audioBitrate}kbps audio` : ''}
								</>
							)}
						</dd>
					</>
				)}
				<dt>Provider</dt>
				<dd>{broadcast?.provider ?? '—'}</dd>
			</dl>

			{!outputs.length ? (
				<p className='health-panel__note'>No RTMP outputs reported yet.</p>
			) : (
				<table className='health-table'>
					<thead>
						<tr>
							<th>Destination</th>
							<th>Status</th>
							<th>Streaming for</th>
						</tr>
					</thead>
					<tbody>
						{outputs.map((output, index) => (
							// eslint-disable-next-line react/no-array-index-key
							<tr key={`${output.url}-${index}`}>
								<td className='health-mono'>{redactStreamUrl(output.url ?? '')}</td>
								<td>
									<StatusPill
										status={streamStatusToProbeStatus(output.status)}
										label={output.status ?? 'unknown'}
									/>
									{output.error && <div className='health-error-text'>{output.error}</div>}
								</td>
								<td className='health-mono'>
									{output.duration
										? formatDurationMs(output.duration)
										: formatTimestamp(output.startTime)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</HealthPanel>
	);
}
